import { useSyncExternalStore } from "react";
import type { ConfiguratorState } from "./types";
import { defaultConfiguratorState, DEFAULT_LOGO_TRANSFORM } from "./types";
import { resolveInitialConfiguratorState } from "./cpbDefaults";

/**
 * Selector-based store backed by `useSyncExternalStore`. This replaces the
 * prior `useState<ConfiguratorState>` in App.tsx so MirrorGroup's children
 * can subscribe to *just the slice they read* (e.g. `s => s.ledColor`)
 * instead of all re-rendering when any field changes. Slice subscriptions
 * use reference equality on the selected value, so primitives short-circuit
 * automatically — for objects (e.g. `s => s.logoTransform`) selectors should
 * return the same nested ref unless the value actually changed (we keep
 * patches structural by spreading shallowly in `setState`).
 *
 * Zero new dependencies — `useSyncExternalStore` is React 18 native.
 */

type Listener = () => void;

let state: ConfiguratorState = defaultConfiguratorState;
const listeners = new Set<Listener>();
let initialized = false;

export function getState(): ConfiguratorState {
  return state;
}

function emit() {
  for (const l of listeners) l();
}

function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/**
 * Apply a flat patch. Mirrors the App.tsx onChange semantics one-to-one:
 *  - bail if every patch field already matches the current state (no-op
 *    re-emit from CPB);
 *  - revoke the old logo `blob:` URL when it gets replaced;
 *  - clear logo state when designMode flips off "logo" or logo URL is nulled.
 */
export function setState(patch: Partial<ConfiguratorState>): void {
  const prev = state;

  let touched = false;
  for (const k in patch) {
    const key = k as keyof ConfiguratorState;
    if (patch[key] !== prev[key]) {
      touched = true;
      break;
    }
  }
  if (!touched) return;

  const revokeIfBlob = (u: string | null) => {
    if (u?.startsWith("blob:")) URL.revokeObjectURL(u);
  };

  let next: ConfiguratorState = { ...prev, ...patch };

  if (patch.logoImageUrl !== undefined && patch.logoImageUrl !== prev.logoImageUrl) {
    revokeIfBlob(prev.logoImageUrl);
  }

  if (patch.designMode !== undefined && patch.designMode !== "logo") {
    revokeIfBlob(prev.logoImageUrl);
    next = {
      ...next,
      logoImageUrl: null,
      logoAssetRevision: "",
      logoTransform: { ...DEFAULT_LOGO_TRANSFORM },
    };
  }

  if (patch.logoImageUrl === null) {
    next = {
      ...next,
      logoAssetRevision: "",
      logoTransform: { ...DEFAULT_LOGO_TRANSFORM },
    };
  }

  state = next;
  emit();
}

/**
 * Updater form for cases where the next state depends on the previous one
 * (logo editor button rotations / scale increments). Same diff/cleanup
 * semantics as `setState`. Returning `prev` from the updater is a no-op.
 */
export function updateState(
  updater: (prev: ConfiguratorState) => ConfiguratorState | null,
): void {
  const next = updater(state);
  if (!next || next === state) return;
  state = next;
  emit();
}

/**
 * Idempotent — pulls the CPB-derived initial snapshot once, then stays put.
 * Called from the App entry; safe to call again (e.g. after HMR).
 */
export function initStore(): void {
  if (initialized) return;
  initialized = true;
  state = resolveInitialConfiguratorState();
  emit();
}

/**
 * Slice subscription hook. The selector runs on every store emission; React
 * only schedules a re-render if the selected value differs from the prior
 * one by reference equality. For derived objects pass an `isEqual` to keep
 * the same identity when content is unchanged.
 */
export function useConfigurator<T>(
  selector: (s: ConfiguratorState) => T,
  isEqual?: (a: T, b: T) => boolean,
): T {
  return useSyncExternalStore(
    subscribe,
    () => selector(state),
    () => selector(state),
  ) as T;
  // The third arg (server snapshot) just mirrors client snapshot — we never
  // SSR this viewer, but React still requires a function in concurrent mode.
  // `isEqual` is reserved for a future micro-opt where we'd memoize the
  // selected value across emits; useSyncExternalStore's built-in identity
  // check is sufficient for our slice patterns today.
  void isEqual;
}

/** Subscribe imperatively (non-React). Used by camera fit + lifecycle hooks. */
export { subscribe as subscribeStore };
